/**
 * StatusPill — canonical status renderer.
 *
 * Never colour-alone: icon + text label + colour token.
 */
import {
    CheckCircle2, AlertCircle, Loader2, Eye, Hourglass,
    OctagonX, Circle, Lock, MinusCircle, CircleDashed,
} from "lucide-react";

export const STATUS_META = {
    COMPLETE: {
        label: "Complete",
        Icon: CheckCircle2,
        cls: "text-emerald-300 bg-emerald-400/[0.07] border-emerald-400/30",
        ringCls: null,
    },
    COMPLETE_WITH_EXCEPTION: {
        label: "Complete w/ Exception",
        Icon: CheckCircle2,
        cls: "text-emerald-300 bg-emerald-400/[0.07] border-emerald-400/30",
        // Amber secondary ring per spec.
        ringCls: "ring-1 ring-amber-300/50",
    },
    IN_PROGRESS: {
        label: "In Progress",
        Icon: Loader2,
        cls: "text-qoryn-cyan bg-qoryn-cyan/[0.08] border-qoryn-cyan/40",
        ringCls: null,
    },
    FOUNDER_REVIEW: {
        label: "Founder Review",
        Icon: Eye,
        cls: "text-amber-300 bg-amber-400/[0.06] border-amber-400/30",
        ringCls: null,
    },
    WAITING_EXTERNAL: {
        label: "Waiting External",
        Icon: Hourglass,
        cls: "text-orange-300 bg-orange-400/[0.06] border-orange-400/30",
        ringCls: null,
    },
    BLOCKED: {
        label: "Blocked",
        Icon: OctagonX,
        cls: "text-red-300 bg-red-400/[0.06] border-red-400/30",
        ringCls: null,
    },
    PLANNED: {
        label: "Planned",
        Icon: Circle,
        cls: "text-purple-300/80 bg-purple-400/[0.05] border-purple-400/25",
        ringCls: null,
    },
    LOCKED: {
        label: "Locked",
        Icon: Lock,
        cls: "text-qoryn-textDim bg-white/[0.03] border-white/10",
        ringCls: null,
    },
    DEFERRED: {
        label: "Deferred",
        Icon: MinusCircle,
        cls: "text-qoryn-textMuted bg-white/[0.03] border-white/10",
        ringCls: null,
    },
    NOT_STARTED: {
        label: "Not Started",
        Icon: CircleDashed,
        cls: "text-qoryn-textDim bg-white/[0.03] border-white/10",
        ringCls: null,
    },
};

export function StatusPill({ status, size = "sm", className = "" }) {
    const meta = STATUS_META[status] ?? STATUS_META.NOT_STARTED;
    const { Icon, label, cls, ringCls } = meta;
    const dims = size === "xs"
        ? "h-5 px-1.5 text-[9px]"
        : size === "md"
            ? "h-7 px-2.5 text-[11px]"
            : "h-6 px-2 text-[10px]";
    const iconSpin = status === "IN_PROGRESS" ? "animate-spin" : "";
    return (
        <span
            data-testid={`status-pill-${status}`}
            className={`inline-flex items-center gap-1.5 rounded-full border font-mono-ui uppercase tracking-widest ${dims} ${cls} ${ringCls ?? ""} ${className}`}
        >
            <Icon className={`h-3 w-3 ${iconSpin}`} strokeWidth={1.75} />
            <span>{label}</span>
        </span>
    );
}

export default StatusPill;
